import React, { useState, useRef, useEffect } from "react";
import { McButton, McIcon, McModal, McTag } from "@maersk-global/mds-react-wrapper/components-core";
import "./RcaQualityPanel.css";

const VERDICT_TAG_APPEARANCE = {
  excellent:    "success",
  strong:       "success",
  acceptable:   "info",
  adequate:     "info",
  "needs work": "warning",
  poor:         "error",
  inadequate:   "error",
};

const getVerdictAppearance = (verdict) => {
  if (!verdict) return "neutral-weak";
  return VERDICT_TAG_APPEARANCE[verdict.toLowerCase().trim()] || "neutral-weak";
};

const VERDICT_BADGE_STYLE = {
  success: { background: "var(--mds_brand_appearance_success_default_background-color,#2C8A4D)", color: "#fff" },
  info:    { background: "var(--mds_brand_appearance_info_default_background-color,#0073ab)",    color: "#fff" },
  warning: { background: "var(--mds_brand_appearance_warning_default_background-color,#b86800)", color: "#fff" },
  error:   { background: "var(--mds_brand_appearance_error_default_background-color,#B80012)",   color: "#fff" },
};

function VerdictBadge({ verdict }) {
  const appearance = getVerdictAppearance(verdict);
  const style = VERDICT_BADGE_STYLE[appearance] || { background: "#e0e0e0", color: "#141414" };
  return (
    <span style={{
      ...style,
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    }}>
      {verdict?.toUpperCase()}
    </span>
  );
}

// Colors are resolved from MDS tokens at runtime via CSS; these drive inline styles on GapCard
// (gap cards need inline bg/border because severity is dynamic — can't predeclare 3 class variants)
const SEVERITY_META = {
  high: { label: "HIGH PRIORITY",   bg: "var(--mds_brand_appearance_error_weak_background-color)",   border: "var(--mds_brand_appearance_error_weak_border-color)",   dot: "var(--mds_brand_appearance_error_default_background-color)",   text: "var(--mds_brand_appearance_error_weak_on-background-color)" },
  med:  { label: "MEDIUM PRIORITY", bg: "var(--mds_brand_appearance_warning_weak_background-color)", border: "var(--mds_brand_appearance_warning_weak_border-color)", dot: "var(--mds_brand_appearance_warning_default_background-color)", text: "var(--mds_brand_appearance_warning_weak_on-background-color)" },
  low:  { label: "LOW PRIORITY",    bg: "var(--mds_brand_appearance_info_weak_background-color)",    border: "var(--mds_brand_appearance_info_weak_border-color)",    dot: "var(--mds_brand_appearance_info_default_background-color)",    text: "var(--mds_brand_appearance_info_weak_on-background-color)" },
};

// Score colours mapped to MDS semantic token values (success / warning / error)
function arcColor(pct) {
  if (pct >= 80) return "var(--mds_brand_appearance_success_default_background-color)";
  if (pct >= 60) return "var(--mds_brand_appearance_warning_weak_on-background-color)";
  if (pct >= 40) return "var(--mds_brand_appearance_warning_default_background-color)";
  return "var(--mds_brand_appearance_error_default_background-color)";
}

function ScoreArc({ score }) {
  const pct   = (score / 10) * 100;
  const color = arcColor(pct);
  const deg   = (score / 10) * 360;
  return (
    <div className="rca-arc" style={{ background: `conic-gradient(${color} ${deg}deg, #e9ecef 0deg)` }}>
      <div className="rca-arc__inner">
        <span className="rca-arc__number" style={{ color }}>{Math.round(score)}</span>
        <span className="rca-arc__denom">/ 10</span>
      </div>
    </div>
  );
}

function DimRow({ dim, detail, onClick, active, hasDetail }) {
  return (
    <div
      className={`rca-dim-card${hasDetail ? " rca-dim-card--clickable" : ""}${active ? " rca-dim-card--active" : ""}`}
      onClick={hasDetail ? onClick : undefined}
      title={hasDetail ? "Click to view detailed insights" : undefined}
    >
      <div className="rca-dim-card__top">
        <div className="rca-dim-card__left">
          <span className="rca-dim-card__label">{dim.label}</span>
        </div>
        <div className="rca-dim-card__right">
          {dim.verdict && <VerdictBadge verdict={dim.verdict} />}
          {hasDetail && (
            <McIcon
              icon={active ? "chevron-up" : "chevron-down"}
              size="16"
              className="rca-dim-card__chevron"
            />
          )}
        </div>
      </div>
      {active && (
        <div className="rca-dim-card__expanded">
          {dim.note && (
            <p className="rca-dim-expanded__note">{dim.note}</p>
          )}
          {detail?.whyLow && (
            <p className="rca-dim-expanded__why">{detail.whyLow}</p>
          )}
          {detail?.missing?.length > 0 && (
            <ul className="rca-dim-expanded__missing">
              {detail.missing.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
          {detail?.recommendations?.length > 0 && (
            <div className="rca-dim-expanded__recs">
              {detail.recommendations.map((r, i) => (
                <div key={i} className="rca-dim-expanded__rec">
                  <div className="rca-dim-expanded__rec-title">{r.title}</div>
                  <div className="rca-dim-expanded__rec-detail">{r.detail}</div>
                  {r.example && (
                    <div className="rca-dim-expanded__rec-example">{r.example}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetailedInsightCard({ insight, id, active }) {
  return (
    <div id={id} className={`rca-insight-card${active ? " rca-insight-card--active" : ""}`}>
      <div className="rca-insight-card__header">
        <span className="rca-insight-card__label">{insight.label}</span>
        {insight.verdict && <VerdictBadge verdict={insight.verdict} />}
      </div>
      {insight.whyLow && (
        <p className="rca-insight-card__why">{insight.whyLow}</p>
      )}
      {insight.missing?.length > 0 && (
        <ul className="rca-insight-card__missing">
          {insight.missing.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
      {insight.recommendations?.length > 0 && (
        <div className="rca-insight-card__recs">
          {insight.recommendations.map((r, i) => (
            <div key={i} className="rca-insight-card__rec">
              <div className="rca-insight-card__rec-title">{r.title}</div>
              <div className="rca-insight-card__rec-detail">{r.detail}</div>
              {r.example && (
                <div className="rca-insight-card__rec-example">{r.example}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StrengthCard({ text }) {
  return (
    <div className="rca-strength-card">
      <div className="rca-strength-card__icon"><McIcon icon="check" size="14" /></div>
      <span className="rca-strength-card__text">{text}</span>
    </div>
  );
}

function GapCard({ gap }) {
  const meta = SEVERITY_META[gap.severity] || SEVERITY_META.low;
  return (
    <div className="rca-gap-card" style={{ background: meta.bg, borderColor: meta.border }}>
      <div className="rca-gap-card__severity" style={{ color: meta.text }}>
        <span className="rca-gap-card__dot" style={{ background: meta.dot }} />
        {meta.label}
      </div>
      <div className="rca-gap-card__title">{gap.title}</div>
      <div className="rca-gap-card__detail">{gap.detail}</div>
    </div>
  );
}

function SuggestionCard({ suggestion }) {
  return (
    <div className="rca-suggestion-card">
      <div className="rca-suggestion-card__icon">
        <McIcon icon="plus-circle" size="14" />
      </div>
      <div className="rca-suggestion-card__body">
        <span className="rca-suggestion-card__section">{suggestion.section}</span>
        <span className="rca-suggestion-card__action">{suggestion.action}</span>
      </div>
    </div>
  );
}

/* ── PDF Export ─────────────────────────────────── */
async function handleExport(data, incidentID, postmortemData) {
  const { pdf } = await import("@react-pdf/renderer");
  const { saveAs } = await import("file-saver");
  const { default: RcaQualityDoc } = await import("./RcaQualityDoc");
  const blob = await pdf(<RcaQualityDoc data={data} incidentID={incidentID} postmortemData={postmortemData} />).toBlob();
  saveAs(blob, `rca-quality-${incidentID || "report"}.pdf`);
}
function handleShare(data, incidentID) {
  const dims = (data.dims || []).map(
    (d) => `  ${d.label}: ${d.score}/${d.maxScore || 10}`
  ).join("\n");
  const text = [
    `AI RCA Quality Report${incidentID ? ` · ${incidentID}` : ""}`,
    `Score: ${Math.round(data.score ?? 0)}/10 (${data.verdict})`,
    data.oneLine,
    "",
    "Dimensions:",
    dims,
  ].join("\n");
  if (navigator.share) {
    navigator.share({ title: "AI RCA Quality Report", text });
  } else {
    navigator.clipboard.writeText(text).then(() => alert("Summary copied to clipboard."));
  }
}

function timeAgo(isoString) {
  if (!isoString) return null;
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60)   return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function RcaModal({ data, incidentID, onClose, blockingMessage, onReanalyse, reanalysing, timestamp, postmortemData, recurrenceContent, onRunQuality, runningQuality }) {
  const [activeKey, setActiveKey] = useState(null);
  const [dimsOpen, setDimsOpen] = useState(true);
  const [insightsOpen, setInsightsOpen] = useState(false);
  // Only show tabs when a recurrence panel is supplied (Report view); the editor's
  // quality-gate modal has no recurrenceContent and keeps its single-view layout.
  const [activeTab, setActiveTab] = useState(recurrenceContent ? "recurrence" : "quality");
  const bodyRef = useRef(null);

  const detailMap = Object.fromEntries((data?.detailedInsights || []).map((ins) => [ins.key, ins]));

  const handleDimClick = (key) => {
    setActiveKey(prev => prev === key ? null : key);
  };

  const hasQualityData = !!data;

  return (
    <McModal
      open={true}
      closed={onClose}
      width="720px"
      height="88vh"
      dimension="medium"
    >
      {/* Heading slot */}
      <span slot="heading" className="rca-modal__heading-slot">
        <div className="rca-modal__header-icon">
          <McIcon icon="sparkle" size="20" />
        </div>
        <div>
          <div className="rca-modal__header-title">AI Analysis</div>
          <div className="rca-modal__header-sub">Postmortem review &amp; recurrence · Powered by GPT-5.4</div>
        </div>
      </span>

      {recurrenceContent && (
        <div className="rca-modal__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "recurrence"}
            className={`rca-modal__tab${activeTab === "recurrence" ? " rca-modal__tab--active" : ""}`}
            onClick={() => setActiveTab("recurrence")}
          >
            Recurrence
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "quality"}
            className={`rca-modal__tab${activeTab === "quality" ? " rca-modal__tab--active" : ""}`}
            onClick={() => setActiveTab("quality")}
          >
            Quality Review
          </button>
        </div>
      )}

      {/* Body */}
      <div className="rca-modal__body" ref={bodyRef}>

        {activeTab === "recurrence" && recurrenceContent}

        {activeTab === "quality" && !hasQualityData && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "flex-start" }}>
            <p style={{ margin: 0, fontSize: "13px", color: "#545454" }}>
              Run the quality analysis to see the postmortem review.
            </p>
            {onRunQuality && (
              <McButton
                appearance="primary"
                variant="outlined"
                fit="small"
                icon="sparkle"
                loading={runningQuality}
                label={runningQuality ? "Analysing…" : "Run Quality Analysis"}
                onClick={onRunQuality}
              />
            )}
          </div>
        )}

        {activeTab === "quality" && hasQualityData && (
        <>
        {/* Blocking gate banner */}
        {blockingMessage && (
          <div className="rca-blocking-banner">
            <McIcon icon="exclamation-triangle-solid" size="16" />
            <span>{blockingMessage}</span>
          </div>
        )}

        {/* Score hero */}
        <div className="rca-hero">
          <span className={`rca-hero__verdict rca-hero__verdict--${getVerdictAppearance(data.verdict)}`}>
            {data.verdict?.toUpperCase() || 'UNKNOWN'}
          </span>
          <p className="rca-hero__oneliner">{data.oneLine}</p>
        </div>

        {/* Dimensions */}
        {data.dims?.length > 0 && (
          <div className="rca-section">
            <div className="rca-section__label" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setDimsOpen(v => !v)}>
              <span>QUALITY BY DIMENSION</span>
              <span className="rca-section__label-hint" style={{ marginLeft: 'auto', color: 'var(--mds_brand_appearance_primary_default_text-color, #0073ab)', fontWeight: 400, fontSize: '12px' }}>
                {dimsOpen ? 'Hide details ▲' : 'Show details ▼'}
              </span>
            </div>
            {dimsOpen && (
              <div className="rca-dims-list">
                {data.dims.map((d) => (
                  <DimRow
                    key={d.key}
                    dim={d}
                    detail={detailMap[d.key]}
                    active={activeKey === d.key}
                    hasDetail={!!detailMap[d.key] || !!d.note}
                    onClick={() => handleDimClick(d.key)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Gaps */}
        {data.gaps?.length > 0 && (
          <div className="rca-section">
            <div className="rca-section__label">GAPS &amp; CRITICAL FINDINGS</div>
            <div className="rca-gaps-list">
              {data.gaps.map((g, i) => <GapCard key={i} gap={g} />)}
            </div>
          </div>
        )}

        {/* Suggestions */}
        {data.suggestions?.length > 0 && (
          <div className="rca-section">
            <div className="rca-section__label">MISSING OR UNDER-DOCUMENTED FIELDS</div>
            <div className="rca-suggestions-list">
              {data.suggestions.map((s, i) => <SuggestionCard key={i} suggestion={s} />)}
            </div>
          </div>
        )}

        {/* Detailed Insights By Section — collapsed by default */}
        {data.detailedInsights?.length > 0 && (
          <div className="rca-section">
            <div className="rca-section__label" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setInsightsOpen(v => !v)}>
              <span>DETAILED INSIGHTS BY SECTION</span>
              <span className="rca-section__label-hint" style={{ marginLeft: 'auto', color: 'var(--mds_brand_appearance_primary_default_text-color, #0073ab)', fontWeight: 400, fontSize: '12px' }}>
                {insightsOpen ? 'Hide details ▲' : 'Show details ▼'}
              </span>
            </div>
            {insightsOpen && (
              <div className="rca-insights-list">
                {data.detailedInsights.map((insight) => (
                  <DetailedInsightCard
                    key={insight.key}
                    insight={insight}
                    id={`rca-detail-${insight.key}`}
                    active={activeKey === insight.key}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        </>
        )}

      </div>

      {/* Footer actions — quality-only actions apply just to the Quality Review tab */}
      {activeTab === "quality" && hasQualityData && onReanalyse && (
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          fit="small"
          icon="sparkle"
          label={reanalysing ? "Analysing…" : "Re-analyse"}
          loading={reanalysing}
          onClick={onReanalyse}
        />
      )}
      {activeTab === "quality" && hasQualityData && timestamp && (
        <span slot="secondaryAction" className="rca-trigger__timestamp" style={{ alignSelf: "center", fontSize: "11px" }}>
          Last analysed: {timeAgo(timestamp)}
        </span>
      )}
      {activeTab === "quality" && hasQualityData && (
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          fit="small"
          icon="file-pdf"
          label="Export PDF"
          onClick={() => handleExport(data, incidentID, postmortemData)}
        />
      )}
      <McButton
        slot="primaryAction"
        appearance="primary"
        fit="small"
        label="Close"
        dialogaction="cancel"
      />
    </McModal>
  );
}

const RCA_CACHE_PREFIX = "rca_quality_";

function readLocalCache(postMortemId) {
  try {
    const raw = localStorage.getItem(RCA_CACHE_PREFIX + postMortemId);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const RCA_CACHE_EVENT = "rca-cache-updated";

function writeLocalCache(postMortemId, data, timestamp) {
  try {
    localStorage.setItem(RCA_CACHE_PREFIX + postMortemId, JSON.stringify({ data, timestamp }));
    window.dispatchEvent(new CustomEvent(RCA_CACHE_EVENT, { detail: { postMortemId } }));
  } catch {}
}

export function useRcaCache(postMortemId) {
  const [cached, setCached] = useState(() => readLocalCache(postMortemId));

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.postMortemId === postMortemId) {
        setCached(readLocalCache(postMortemId));
      }
    };
    window.addEventListener(RCA_CACHE_EVENT, handler);
    return () => window.removeEventListener(RCA_CACHE_EVENT, handler);
  }, [postMortemId]);

  return { data: cached?.data || null, timestamp: cached?.timestamp || null };
}

export function AiSuggestionsInline({ insight, sectionName, dim, onClose }) {
  if (!insight) return null;

  const recs    = insight.recommendations || [];
  const missing = insight.missing || [];
  if (recs.length === 0 && missing.length === 0 && !insight.whyLow) return null;

  return (
    <div className="ai-inline-panel">
      <div className="ai-inline-panel__header">
        <span className="ai-inline-panel__header-icon">✦</span>
        <div className="ai-inline-panel__title" style={{ flex: 1 }}>AI Review</div>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#ffffff",
              padding: "2px 4px",
              display: "flex",
              alignItems: "center",
              fontSize: "16px",
              lineHeight: 1,
              opacity: 0.85,
            }}
            aria-label="Close AI Review"
          >
            ×
          </button>
        )}
      </div>
      <div className="ai-inline-panel__body">

        {/* Dimension summary note from Quality by Dimension */}
        {dim?.note && (
          <div className="ai-inline-dim-summary">
            <p className="ai-inline-dim-summary__note">{dim.note}</p>
          </div>
        )}

        {insight.whyLow && (
          <p className="ai-inline-panel__why">{insight.whyLow}</p>
        )}

        {missing.length > 0 && (
          <div className="ai-inline-section">
            <div className="ai-inline-section__label">What's missing</div>
            <ul className="ai-inline-missing-list">
              {missing.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {recs.length > 0 && (
          <div className="ai-inline-recs">
            {recs.map((r, i) => (
              <div key={i} className="ai-inline-rec">
                <div className="ai-inline-rec__title">{r.title}</div>
                <div className="ai-inline-rec__detail">{r.detail}</div>
                {r.example && <div className="ai-inline-rec__example">{r.example}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function useRcaAnalysis({ incidentID, postMortemId, fetchQuality, fetchEnhancements }) {
  const [data, setData]                   = useState(null);
  const [timestamp, setTimestamp]         = useState(null);
  const [loading, setLoading]             = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!postMortemId);
  const [error, setError]                 = useState(null);

  useEffect(() => {
    if (!postMortemId) { setInitialLoading(false); return; }

    // 1. Try localStorage first — instant, no network
    const cached = readLocalCache(postMortemId);
    if (cached?.data) {
      setData(cached.data);
      setTimestamp(cached.timestamp || null);
      setInitialLoading(false);
      return;
    }

    // 2. Fall back to enhancements endpoint (backend-persisted)
    if (!fetchEnhancements) { setInitialLoading(false); return; }
    fetchEnhancements(postMortemId)
      .then((enhancement) => {
        if (enhancement?.rcaAnalysis) {
          setData(enhancement.rcaAnalysis);
          setTimestamp(enhancement.rcaAnalysisTimestamp || null);
          writeLocalCache(postMortemId, enhancement.rcaAnalysis, enhancement.rcaAnalysisTimestamp || null);
        }
      })
      .catch(() => {})
      .finally(() => setInitialLoading(false));
  }, [postMortemId]);

  const runAnalysis = async (forceRefresh = false, onSuccess) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchQuality(incidentID, forceRefresh);
      const ts = new Date().toISOString();
      setData(result);
      setTimestamp(ts);
      writeLocalCache(postMortemId, result, ts);
      onSuccess?.();
    } catch {
      setError("Analysis failed — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return { data, timestamp, loading, initialLoading, error, runAnalysis };
}

export default function RcaQualityPanel({ incidentID, postMortemId, fetchQuality, fetchEnhancements, postmortemData, buttonAppearance = "inverse", buttonFit = "small", recurrenceContent }) {
  const [open, setOpen] = useState(false);
  const { data, timestamp, loading, initialLoading, error, runAnalysis } = useRcaAnalysis({
    incidentID, postMortemId, fetchQuality, fetchEnhancements,
  });

  const hasCached = !!data;
  // With a recurrence tab to show, the button should open the modal even before
  // a quality review exists — quality analysis becomes an in-modal action instead.
  const canOpenWithoutQuality = !!recurrenceContent;

  return (
    <>
      <div className="rca-trigger">
        {initialLoading ? (
          <McButton appearance={buttonAppearance} variant="outlined" fit={buttonFit} icon="sparkle" label="Loading…" disabled />
        ) : (hasCached || canOpenWithoutQuality) ? (
          <McButton
            appearance={buttonAppearance}
            variant="outlined"
            fit={buttonFit}
            icon="sparkle"
            label="AI Analysis"
            onClick={() => setOpen(true)}
          />
        ) : (
          <McButton
            appearance={buttonAppearance}
            variant="outlined"
            fit={buttonFit}
            loading={loading}
            label={loading ? "Analysing…" : "Run AI Analysis"}
            icon="sparkle"
            onClick={() => runAnalysis(false, () => setOpen(true))}
          />
        )}
        {error && <span className="rca-trigger__error">{error}</span>}
      </div>
      {open && (hasCached || canOpenWithoutQuality) && (
        <RcaModal
          data={data}
          incidentID={incidentID}
          onClose={() => setOpen(false)}
          onReanalyse={hasCached ? () => runAnalysis(true) : undefined}
          onRunQuality={!hasCached ? () => runAnalysis(false) : undefined}
          runningQuality={loading}
          reanalysing={loading}
          timestamp={timestamp}
          postmortemData={postmortemData}
          recurrenceContent={recurrenceContent}
        />
      )}
    </>
  );
}
